import { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { AuthShell, Field } from "@/pages/Login";
import { Envelope, CheckCircle } from "@phosphor-icons/react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [devLink, setDevLink] = useState(null);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const r = await api.post("/auth/forgot-password", { email });
      setSent(true);
      // dev_link only present when backend is running without RESEND_API_KEY.
      if (r.data?.dev_link) setDevLink(r.data.dev_link);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Could not send the reset email.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthShell>
      <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58] mb-2">
        Account Recovery
      </div>
      <h1 className="font-serif-display text-4xl text-[#0A192F] leading-tight mb-1">Forgot password?</h1>
      <p className="text-sm text-[#5C5F58] mb-6">
        Enter the email on your ledger and we&apos;ll send a reset link.
      </p>

      {sent ? (
        <div data-testid="forgot-success" className="space-y-4">
          <div className="flex items-start gap-3 border border-[#1A3622]/30 bg-[#1A3622]/5 p-4">
            <CheckCircle size={20} weight="regular" className="text-[#1A3622] mt-0.5 shrink-0" />
            <div className="text-sm text-[#0A192F]">
              <p className="font-medium">Check your inbox.</p>
              <p className="text-xs text-[#5C5F58] mt-1">
                If an account exists for <strong className="text-[#0A192F]">{email}</strong>, a reset link is on its way.
                The link expires in 30 minutes.
              </p>
            </div>
          </div>
          {devLink && (
            <div
              data-testid="forgot-dev-link"
              className="border border-dashed border-[#C9C4B8] bg-[#F9F8F6] p-3 text-xs text-[#5C5F58]"
            >
              <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] mb-1">
                Dev mode (no RESEND_API_KEY set)
              </div>
              <a href={devLink} className="text-[#1A3622] underline break-all">
                {devLink}
              </a>
            </div>
          )}
          <Link
            to="/login"
            className="block text-center text-xs text-[#1A3622] hover:underline font-mono-ledger uppercase tracking-[0.25em]"
            data-testid="forgot-back-to-login"
          >
            Back to sign in
          </Link>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
          <Field icon={<Envelope size={14} />} label="Email">
            <input
              data-testid="forgot-email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-white border border-[#E0DDD6] pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:border-[#1A3622]"
            />
          </Field>

          {error && (
            <div className="text-xs text-[#6B1718] bg-[#6B1718]/5 border border-[#6B1718]/30 px-3 py-2" data-testid="forgot-error">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            data-testid="forgot-submit"
            className="w-full bg-[#1A3622] text-[#F9F8F6] py-3 text-sm font-medium border border-[#0F2818] hover:bg-[#0F2818] disabled:opacity-60"
          >
            {submitting ? "Sending…" : "Send reset link"}
          </button>

          <p className="text-xs text-[#5C5F58] mt-6 text-center">
            Remembered it?{" "}
            <Link to="/login" className="underline underline-offset-4 text-[#1A3622]" data-testid="forgot-link-login">
              Sign in
            </Link>
          </p>
        </form>
      )}
    </AuthShell>
  );
}
